import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, Trash2, Ban, Printer, RotateCcw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const today10 = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PAY_TYPES = ["Cash", "UPI", "Card", "Bank transfer", "Cheque", "Other"];
const PARTICULARS = ["Service charge & items purchased", "Housekeeping", "Maintenance", "Utilities", "Staff salary", "Supplies", "Marketing", "Other"];

const emptyCharge = () => ({ particular: "Service charge & items purchased", comments: "", amount: "", ref_no: "" });
const emptyPayment = () => ({ type: "Cash", comments: "", amount: "", ref_no: "" });

export default function ExpensesTab({ pid, refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opDay, setOpDay] = useState(today10());  // Batch-2 Item 6: property Operating Day, fetched below
  const [form, setForm] = useState({ name: "", date: today10(), prepared_by: "", charges: [emptyCharge()], payments: [emptyPayment()] });
  const [hideVoid, setHideVoid] = useState(true);

  // Batch-2 Item 6: fetch the property's current Operating Day so voucher
  // dates default to it (not the real calendar date). One extra small call
  // per pid change; reservations endpoint already returns operating_day.
  useEffect(() => {
    api.get(`/pms/properties/${pid}/reservations`).then(({ data: d }) => {
      if (d?.operating_day) {
        setOpDay(d.operating_day);
        setForm((f) => ({ ...f, date: d.operating_day }));
      }
    }).catch(() => {});
  }, [pid]);

  const load = useCallback(() => {
    setData(null);
    api.get(`/pms/properties/${pid}/vouchers`, { params: { include_void: !hideVoid } })
      .then(({ data: d }) => {
        setData(d);
        if (d.vouchers.length) setSelected((prev) => d.vouchers.find((v) => v.id === prev?.id) || d.vouchers[0]);
        else setSelected(null);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [pid, hideVoid]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const totalCharges = form.charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalPayments = form.payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balance = totalCharges - totalPayments;

  const submit = async () => {
    const charges = form.charges.filter((c) => parseFloat(c.amount) > 0).map((c) => ({ ...c, amount: parseFloat(c.amount) }));
    if (charges.length === 0) return toast.error("Add at least one charge with an amount");
    const payments = form.payments.filter((p) => parseFloat(p.amount) > 0).map((p) => ({ ...p, amount: parseFloat(p.amount) }));
    setBusy(true);
    try {
      const { data: v } = await api.post(`/pms/properties/${pid}/vouchers`, { ...form, charges, payments });
      toast.success(`Voucher #${v.voucher_no} saved — ₹${fmt(v.total_charges)}. Feeds into this property's P&L.`);
      setDrawer(false);
      setForm({ name: "", date: opDay, prepared_by: "", charges: [emptyCharge()], payments: [emptyPayment()] });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const voidVoucher = async (v) => {
    try {
      const { data: r } = await api.post(`/pms/vouchers/${v.id}/void`);
      toast.success(r.status === "void" ? `Voucher #${v.voucher_no} voided` : `Voucher #${v.voucher_no} restored`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteVoucher = async (v) => {
    if (!window.confirm(`Delete voucher #${v.voucher_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/pms/vouchers/${v.id}`);
      toast.success(`Voucher #${v.voucher_no} deleted`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  return (
    <div className="reveal">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-slate-900 text-lg">Expense Vouchers</h2>
          <p className="text-[11px] text-slate-500">Every charge line rolls into this property's P&L · voided vouchers are excluded from totals</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500 inline-flex items-center gap-1.5"><input type="checkbox" checked={hideVoid} onChange={(e) => setHideVoid(e.target.checked)} /> Hide Void</label>
          <button data-testid="voucher-add-new" onClick={() => setDrawer(true)} className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2"><Plus size={13} /> Add New</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <span>Voucher · Details</span><span>Charges (₹)</span>
          </div>
          <div className="divide-y divide-slate-50 max-h-[65vh] overflow-y-auto">
            {data.vouchers.length === 0 && <p className="px-4 py-10 text-center text-xs text-slate-400">No vouchers yet.</p>}
            {data.vouchers.map((v) => (
              <button key={v.id} data-testid={`voucher-item-${v.voucher_no}`} onClick={() => setSelected(v)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50/70 transition-colors flex justify-between items-start gap-3 ${selected?.id === v.id ? "bg-[#EEF2FF]/60 border-l-4 border-l-[#4F46E5]" : "border-l-4 border-l-transparent"}`}>
                <div className="min-w-0">
                  <p className="font-mono font-bold text-slate-900 text-sm">{v.voucher_no} {v.status === "void" && <span className="text-red-500 ml-1 text-[10px] font-sans">VOID</span>}</p>
                  <p className="text-xs text-slate-600 truncate">{v.name}</p>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">{v.date}</p>
                </div>
                <span className="font-mono font-bold text-slate-800 text-sm shrink-0">{fmt(v.total_charges)}</span>
              </button>
            ))}
          </div>
          {data.vouchers.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 text-xs flex justify-between">
              <span className="text-slate-500 font-semibold uppercase tracking-wider">Total (active)</span>
              <span className="font-mono font-bold text-slate-900">₹{fmt(data.total_charges)}</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {selected ? (
            <>
              <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-mono font-bold text-lg text-slate-900">{selected.voucher_no}</p>
                  <p className="text-sm text-slate-600">{selected.name}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid="voucher-void" onClick={() => voidVoucher(selected)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-full hover:border-slate-400 flex items-center gap-1.5 text-slate-600">{selected.status === "void" ? <><RotateCcw size={12} /> Restore</> : <><Ban size={12} /> Void</>}</button>
                  <button data-testid="voucher-delete" onClick={() => deleteVoucher(selected)} className="text-xs px-3 py-1.5 border border-red-200 text-red-500 rounded-full hover:bg-red-50 flex items-center gap-1.5"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
              <div className="px-6 py-4 grid grid-cols-3 gap-4 text-xs border-b border-slate-100 bg-slate-50/40">
                <div><p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Voucher Date</p><p className="font-mono text-slate-700 mt-0.5">{selected.date}</p></div>
                <div><p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Prepared By</p><p className="text-slate-700 mt-0.5">{selected.prepared_by}</p></div>
                <div><p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Total Charges</p><p className="font-mono text-slate-700 mt-0.5">₹ {fmt(selected.total_charges)}</p></div>
              </div>

              <div className="px-6 py-5">
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider mb-2">Charges</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="text-left py-2 font-semibold w-14">Sr</th><th className="text-left font-semibold">Ref No</th>
                    <th className="text-left font-semibold">Particular</th><th className="text-left font-semibold">Comments</th>
                    <th className="text-right font-semibold">Amount (₹)</th>
                  </tr></thead>
                  <tbody>
                    {selected.charges.map((c) => (
                      <tr key={c.sr} className="border-b border-slate-50">
                        <td className="py-2 font-mono text-slate-500">{c.sr}</td>
                        <td className="py-2 text-slate-500">{c.ref_no || "-"}</td>
                        <td className="py-2 text-slate-700">{c.particular}</td>
                        <td className="py-2 text-slate-500">{c.comments || "-"}</td>
                        <td className="py-2 text-right font-mono text-slate-700">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {selected.payments.length > 0 && (
                  <>
                    <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider mb-2 mt-6">Payments</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                        <th className="text-left py-2 font-semibold w-14">Sr</th><th className="text-left font-semibold">Ref No</th>
                        <th className="text-left font-semibold">Type</th><th className="text-left font-semibold">Comments</th>
                        <th className="text-right font-semibold">Amount (₹)</th>
                      </tr></thead>
                      <tbody>
                        {selected.payments.map((p) => (
                          <tr key={p.sr} className="border-b border-slate-50">
                            <td className="py-2 font-mono text-slate-500">{p.sr}</td>
                            <td className="py-2 text-slate-500">{p.ref_no || "-"}</td>
                            <td className="py-2 text-slate-700">{p.type}</td>
                            <td className="py-2 text-slate-500">{p.comments || "-"}</td>
                            <td className="py-2 text-right font-mono text-red-500">-{fmt(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                <div className="mt-6 flex justify-end">
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Balance</p>
                    <p className={`font-mono font-bold text-lg ${selected.balance <= 0 ? "text-emerald-600" : "text-red-500"}`}>₹ {fmt(selected.balance)}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-24 text-center text-sm text-slate-400">Select a voucher or add a new one.</div>
          )}
        </div>
      </div>

      {drawer && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={() => setDrawer(false)}>
          <div data-testid="voucher-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <h3 className="font-display font-bold text-lg">New Expense Voucher</h3>
              <button data-testid="voucher-drawer-close" onClick={() => setDrawer(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Name / Person</label>
                <input data-testid="voucher-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Shaad" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Voucher Date</label>
                <input data-testid="voucher-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none" /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Prepared By</label>
                <input data-testid="voucher-prepared-by" value={form.prepared_by} onChange={(e) => setForm({ ...form, prepared_by: e.target.value })} placeholder="Auto: host" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none" /></div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[11px] uppercase font-semibold tracking-wider text-slate-500">Charges</p>
                <button data-testid="voucher-add-charge" onClick={() => setForm({ ...form, charges: [...form.charges, emptyCharge()] })} className="text-xs text-[#4F46E5] font-semibold flex items-center gap-1"><Plus size={12} /> Add line</button>
              </div>
              <div className="space-y-2">
                {form.charges.map((c, i) => (
                  <div key={i} className="grid grid-cols-[36px,90px,1fr,1fr,120px,28px] gap-2 items-center">
                    <span className="font-mono text-xs text-slate-400">{i + 1}</span>
                    <input placeholder="Ref" value={c.ref_no} onChange={(e) => { const cs = [...form.charges]; cs[i].ref_no = e.target.value; setForm({ ...form, charges: cs }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <select value={c.particular} onChange={(e) => { const cs = [...form.charges]; cs[i].particular = e.target.value; setForm({ ...form, charges: cs }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
                      {PARTICULARS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                    <input placeholder="Comments" value={c.comments} onChange={(e) => { const cs = [...form.charges]; cs[i].comments = e.target.value; setForm({ ...form, charges: cs }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <input data-testid={`voucher-charge-amount-${i}`} placeholder="Amount ₹" inputMode="decimal" value={c.amount} onChange={(e) => { const cs = [...form.charges]; cs[i].amount = e.target.value.replace(/[^\d.]/g, ""); setForm({ ...form, charges: cs }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                    <button onClick={() => setForm({ ...form, charges: form.charges.filter((_, j) => j !== i) })} disabled={form.charges.length === 1} className="text-slate-300 hover:text-red-500 disabled:opacity-30"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[11px] uppercase font-semibold tracking-wider text-slate-500">Payments (optional)</p>
                <button data-testid="voucher-add-payment" onClick={() => setForm({ ...form, payments: [...form.payments, emptyPayment()] })} className="text-xs text-[#4F46E5] font-semibold flex items-center gap-1"><Plus size={12} /> Add line</button>
              </div>
              <div className="space-y-2">
                {form.payments.map((p, i) => (
                  <div key={i} className="grid grid-cols-[36px,90px,1fr,1fr,120px,28px] gap-2 items-center">
                    <span className="font-mono text-xs text-slate-400">{i + 1}</span>
                    <input placeholder="Ref" value={p.ref_no} onChange={(e) => { const ps = [...form.payments]; ps[i].ref_no = e.target.value; setForm({ ...form, payments: ps }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <select value={p.type} onChange={(e) => { const ps = [...form.payments]; ps[i].type = e.target.value; setForm({ ...form, payments: ps }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
                      {PAY_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    <input placeholder="Comments" value={p.comments} onChange={(e) => { const ps = [...form.payments]; ps[i].comments = e.target.value; setForm({ ...form, payments: ps }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <input data-testid={`voucher-payment-amount-${i}`} placeholder="Amount ₹" inputMode="decimal" value={p.amount} onChange={(e) => { const ps = [...form.payments]; ps[i].amount = e.target.value.replace(/[^\d.]/g, ""); setForm({ ...form, payments: ps }); }} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                    <button onClick={() => setForm({ ...form, payments: form.payments.filter((_, j) => j !== i) })} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 bg-slate-50 rounded-xl p-4 grid grid-cols-3 gap-4 text-sm">
              <div><p className="text-[10px] uppercase text-slate-400 font-semibold">Total Charges</p><p className="font-mono font-bold text-slate-900 mt-1">₹ {fmt(totalCharges)}</p></div>
              <div><p className="text-[10px] uppercase text-slate-400 font-semibold">Total Payments</p><p className="font-mono font-bold text-slate-700 mt-1">₹ {fmt(totalPayments)}</p></div>
              <div><p className="text-[10px] uppercase text-slate-400 font-semibold">Balance</p><p className={`font-mono font-bold mt-1 ${balance <= 0 ? "text-emerald-600" : "text-red-500"}`}>₹ {fmt(balance)}</p></div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDrawer(false)} className="px-5 py-2.5 rounded-full text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
              <button data-testid="voucher-save" onClick={submit} disabled={busy || totalCharges <= 0} className="btn-brand rounded-full px-6 py-2.5 text-sm font-semibold flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={14} /> : null} Save voucher</button>
            </div>
          </div>
        </div>
      </Portal>
      )}
    </div>
  );
}
